import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { NAV_LINKS, OM_SERVICE, SERVICES } from "@/data/content";

const LOGO_CLEAR = "/brand/evovlex-logo-transparent.png";

const linkBase =
  "relative font-sans text-sm font-semibold tracking-wide transition-colors duration-200";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleCloseMega = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 140);
  };

  const lightMode = scrolled && !mobileOpen;

  const navLinkClass = ({ isActive }) =>
    `${linkBase} ${
      lightMode
        ? isActive
          ? "text-evo-blue"
          : "text-slate-700 hover:text-evo-blue"
        : isActive
          ? "text-cyan-300"
          : "text-slate-200 hover:text-white"
    }`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          mobileOpen
            ? "bg-navy-950"
            : scrolled
              ? "border-b border-slate-200/80 bg-white/90 shadow-[0_8px_30px_rgba(2,6,23,0.06)] backdrop-blur-xl"
              : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link to="/" data-testid="header-logo-link" aria-label="EVOVLEX IT — Home" className="flex shrink-0 items-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-white p-1 shadow-md ring-1 ring-slate-200/70 sm:h-16 sm:w-16 lg:h-[72px] lg:w-[72px]">
              <img src={LOGO_CLEAR} alt="EVOVLEX IT" className="h-full w-full object-contain" />
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((link) =>
              link.hasMenu ? (
                <div key={link.label} className="relative" onMouseEnter={openMega} onMouseLeave={scheduleCloseMega}>
                  <div className="flex items-center gap-1.5">
                    <NavLink to={link.to} data-testid="nav-link-services" className={navLinkClass}>
                      {link.label}
                    </NavLink>
                    <button
                      type="button"
                      aria-label="Open services menu"
                      aria-expanded={megaOpen}
                      data-testid="nav-mega-menu-trigger"
                      onClick={() => setMegaOpen((v) => !v)}
                      className={`transition-colors ${lightMode ? "text-slate-500 hover:text-evo-blue" : "text-slate-300 hover:text-white"}`}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.label}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={navLinkClass}
                >
                  {link.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-talk-to-expert"
              className="hidden items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-cyan-500/30 lg:inline-flex"
            >
              Talk to an Expert
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-lg border transition-colors lg:hidden ${
                lightMode
                  ? "border-slate-200 text-slate-800 hover:bg-slate-100"
                  : "border-slate-700 text-white hover:bg-navy-800"
              }`}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {megaOpen && (
            <motion.div
              key="mega"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={openMega}
              onMouseLeave={scheduleCloseMega}
              className="absolute inset-x-0 top-full hidden lg:block"
              data-testid="services-mega-menu"
            >
              <div className="mx-auto w-full max-w-7xl px-4 pb-6 pt-2 sm:px-6 lg:px-12">
                <div className="grid grid-cols-12 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-navy-950/25">
                  <div className="col-span-8 p-6">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-600">
                      Technology Solutions
                    </p>
                    <div className="mt-4 grid grid-cols-2 gap-1.5">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`mega-link-${s.slug}`}
                          className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-slate-50"
                        >
                          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-evo-blue transition-colors group-hover:bg-evo-blue group-hover:text-white">
                            <s.icon className="h-4 w-4" strokeWidth={1.8} />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-slate-900 group-hover:text-evo-blue">
                              {s.title}
                            </span>
                            <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-slate-500">
                              {s.short}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="relative col-span-4 overflow-hidden bg-navy-950 p-6 text-white">
                    <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
                    <div className="relative">
                      <span className="inline-flex rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                        Operational & Workforce
                      </span>
                      <Link
                        to={`/services/${OM_SERVICE.slug}`}
                        data-testid="mega-link-om-manpower"
                        className="group mt-5 block rounded-xl border border-slate-800 bg-navy-900/60 p-5 transition-colors hover:border-orange-500/50"
                      >
                        <OM_SERVICE.icon className="h-6 w-6 text-orange-400" strokeWidth={1.8} />
                        <span className="mt-3 block font-display text-lg font-semibold">{OM_SERVICE.title}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-slate-400">{OM_SERVICE.tagline}</span>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                          Explore O&M Services
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      </Link>
                      <Link
                        to="/services"
                        data-testid="mega-link-all-services"
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 hover:text-cyan-300"
                      >
                        View all services
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 overflow-y-auto bg-navy-950 pb-12 pt-24 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-1 px-6">
              {NAV_LINKS.map((link) =>
                link.hasMenu ? (
                  <div key={link.label}>
                    <div className="flex items-center justify-between">
                      <NavLink
                        to={link.to}
                        data-testid="mobile-nav-link-services"
                        className="py-3 font-display text-2xl font-semibold text-white"
                      >
                        {link.label}
                      </NavLink>
                      <button
                        type="button"
                        aria-label="Toggle services submenu"
                        aria-expanded={mobileServicesOpen}
                        data-testid="mobile-services-accordion-toggle"
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-800 text-slate-300"
                      >
                        <ChevronDown className={`h-5 w-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <p className="pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cyan-500">
                            Technology Solutions
                          </p>
                          {SERVICES.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              data-testid={`mobile-nav-link-${s.slug}`}
                              className="block border-b border-slate-800/60 py-3 pl-3 text-sm font-medium text-slate-300 hover:text-white"
                            >
                              {s.title}
                            </Link>
                          ))}
                          <p className="pt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-orange-400">
                            Operational & Workforce Solutions
                          </p>
                          <Link
                            to={`/services/${OM_SERVICE.slug}`}
                            data-testid="mobile-nav-link-om-manpower"
                            className="block border-b border-slate-800/60 py-3 pl-3 text-sm font-semibold text-orange-300"
                          >
                            {OM_SERVICE.title}
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <NavLink
                    key={link.label}
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `py-3 font-display text-2xl font-semibold ${isActive ? "text-cyan-400" : "text-white"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                ),
              )}
              <Link
                to="/contact"
                data-testid="mobile-cta-talk-to-expert"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 text-sm font-semibold text-white"
              >
                Talk to an Expert
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
